import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getDateFromToFilterFields, getInstituteTypeFilterField } from '@/utils/filterFields';
import { getUpcomingSearch } from '@/utils/upcomingSearch';
import { donationRequisitionsInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { DonationRequisitionFormDialog } from '../components/DonationRequisitionFormDialog';
import { getRequisitionColumns } from '../components/MpP2Columns';
import { useDonationRequisitionFilters } from '../hooks/useDonationRequisitionFilters';
import type { DonationRequisition } from '../types/marketPromotionP2.types';

export function DonationRequisitionPage() {
  const controller = useDonationRequisitionFilters();
  const { search } = controller;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editing, setEditing] = useState<DonationRequisition | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    donationRequisitionsInfiniteQueryOptions(search),
  );
  const { data: instituteTypes } = useQuery(activeInstituteTypesQueryOptions());

  const columns = getRequisitionColumns({
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
  });

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Donation Requisition"
        titleAddon={<DateRangeBadge dateFrom={search.dateFrom} dateTo={search.dateTo} />}
        search={getUpcomingSearch('Institute, Teacher, Specimen')}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={{
          label: 'Raise Requisition',
          onClick: () => {
            setEditing(null);
            setFormOpen(true);
          },
        }}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[
            ...getDateFromToFilterFields(),
            getInstituteTypeFilterField(instituteTypes, 'instituteTypeNo'),
          ]}
          values={controller.filterValues}
          defaultValues={controller.defaultFilterValues}
          onChange={controller.handleFilterChange}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the requisitions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="institute"
          emptyMessage="No requisition matches"
          {...loadMoreProps}
        />
      )}

      <DonationRequisitionFormDialog
        key={`${editing?.id ?? 'new'}:${String(isFormOpen)}`}
        requisition={editing}
        open={isFormOpen}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}
